import { ExploreSearchBar } from '@/components/explore/ExploreSearchBar';
import { LayoutSidebar } from '@/components/layouts/LayoutSidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu.tsx';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { BillingNotice } from '@/components/billing/BillingNotice';
import { AsyncQueue } from '@/features/content/AsyncQueue';
import { Speak2MePanel } from '@/features/speak2me/Speak2MePanel';
import { useAuthData, useLogout, useNavigateWithContentFlush, useSetMember, useUser } from '@/hooks';
import { useSearchStore } from '@/store/searchStore';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { LANDING_FILTER_STORAGE_KEY } from '@/utils/auth-utils';
import { getMemberDisplayName } from '@/types/member.ts';
import { Member } from '@vivport/backend/types';
import { Menu, Settings, Shield } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useMatch } from 'react-router-dom';

export default function LayoutHeader() {
  const authData = useAuthData();
  const logoutMutation = useLogout();
  const setMemberMutation = useSetMember();
  const navigate = useNavigateWithContentFlush();
  const isAdminRoute = useMatch('/admin') !== null;
  const isSettingsRoute = useMatch('/settings/*') !== null;
  const memberContextQuery = useUser();
  const isOpen = useSpeak2MeStore((state) => state.isOpen);
  const isHubAdmin = memberContextQuery.data?.isHubAdmin ?? false;
  const setAllTabFilters = useSearchStore((state) => state.setAllTabFilters);
  const location = useLocation();

  // Compute allTabFilters once and store it for all child components to use
  // Note: member context is guaranteed to exist when this component renders (ProtectedLayout guards for auth)
  const allTabFilters = useMemo(() => {
    if (!memberContextQuery.data) return [];
    return memberContextQuery.data.accountTabs
      .map(tab => tab.searchFilter.replace(/^tab:/, "").trim())
      .filter(Boolean)
      .sort();
  }, [memberContextQuery.data]);

  // Store allTabFilters in the store so child components can access it; clear when tabs empty
  useEffect(() => {
    setAllTabFilters(allTabFilters.length > 0 ? allTabFilters : []);
  }, [allTabFilters, setAllTabFilters]);

  if (authData.isLoading) {
    return <div>Loading...</div>;
  }

  if (!authData.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const { account, member, members } = authData;

  const visibleMembers = members?.filter(
    (m) => !account?.hidePersonalMembers || !m.isPersonal || m.id === member?.id
  );

  const handleMemberChange = async (selectedMember: Member) => {
    setMemberMutation.mutate(selectedMember, {
      onSuccess: () => {
        // Product decision: a configured landing view makes switching a
        // "take me to what matters" action; without one, switching stays in place.
        if (selectedMember.landingFilterId !== null) {
          sessionStorage.setItem(LANDING_FILTER_STORAGE_KEY, String(selectedMember.landingFilterId));
          window.location.assign('/explore');
          return;
        }
        // Refresh the page to ensure all data is updated based on the new member
        window.location.reload();
      }
    });
  };

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        navigate('/login', { replace: true });
      }
    });
  };

  return (
    <TooltipProvider>
      <div
        data-testid="layout-header"
        className="flex h-screen min-h-0 w-full min-w-0 max-w-full overflow-hidden bg-background"
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b p-2 lg:px-4">
          <div className="flex items-center">
            <div className="w-[274px] items-start hidden lg:flex">
              <div className="flex items-center gap-2">
                <img src="/logo192.png" className="h-6 w-6 object-contain" alt="Logo" />
                <img src="/vivport-logo.png" className="h-5 translate-y-[3px] object-contain" alt={'Vivport'} />
              </div>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden mr-2">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[240px]" closeButton={false}>
                <SheetHeader className="sr-only">
                  <SheetTitle>Navigation menu</SheetTitle>
                  <SheetDescription>Primary app navigation links and workspace sections.</SheetDescription>
                </SheetHeader>
                <div className="pr-2 pt-2">
                  <div className="flex items-center gap-2">
                    <img src="/logo192.png" className="h-6 w-6 object-contain" alt="Logo" />
                    <img src="/vivport-logo.png" className="h-5 translate-y-[3px] object-contain" alt={'Vivport'} />
                  </div>
                  <Separator className="mt-4 mb-2" />
                  <LayoutSidebar />
                </div>
              </SheetContent>
            </Sheet>
          </div>
          <div className="flex w-full items-center gap-1">
            <ExploreSearchBar />
            <div className="flex items-center gap-0">
              {!isOpen && <AsyncQueue />}
              
              {/* Hub Admin Icon */}
              {isHubAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 ${
                        isAdminRoute
                          ? 'text-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => {
                        navigate('/admin');
                      }}
                    >
                      <Shield className="h-5 w-5" />
                      <span className="sr-only">Hub Admin</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Hub Admin</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Settings Icon */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-8 w-8 ${
                      isSettingsRoute
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => {
                      navigate('/settings');
                    }}
                  >
                    <Settings className="h-5 w-5" />
                    <span className="sr-only">Settings</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Settings</p>
                </TooltipContent>
                </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger data-testid="layout-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="/placeholder-user.jpg" alt="User" />
                    <AvatarFallback>U</AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="mr-4 min-w-[170px]">
                  <div className="px-2 pt-1.5 pb-0.5 text-sm">
                    {member && getMemberDisplayName(member, account)}
                  </div>
                  <div className="px-2 pb-1.5 pt-0 text-sm !text-muted-foreground !italic">
                    {`${member?.hubName || 'Unknown'}${member?.isPersonal ? " (Personal)" : member?.isAdmin ? " (Admin)" : ""}`}
                  </div>

                  {account && account.isAccessible === true && (
                    <>
                      <DropdownMenuSeparator />
                      <div className="px-2 pb-1 pt-1 text-xs font-medium">
                        View as:
                      </div>
                      {visibleMembers?.map((m) => (
                        <DropdownMenuItem
                          key={m.id}
                          className={member?.id === m.id ? 'bg-accent' : ''}
                          onClick={() => handleMemberChange(m)}
                          disabled={setMemberMutation.isPending}
                        >
                          <div className="flex flex-col">
                            <span className="text-sm">{getMemberDisplayName(m, account)}</span>
                            <span className="text-xs text-muted-foreground">
                              {`${m.hubName || 'Unknown'}${m.isPersonal ? " (Personal)" : m.isAdmin ? " (Admin)" : ""}`}
                            </span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    data-testid="layout-logout-btn" 
                    className={'text-destructive mt-2'} 
                    onClick={handleLogout}
                    disabled={logoutMutation.isPending}
                  >
                    {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          <Outlet key={location.pathname} />
        </div>
        </div>
        <Speak2MePanel />
        <BillingNotice />
      </div>
    </TooltipProvider>
  );
}
